import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Layout, LogOut, CreditCard, Puzzle, ShieldCheck } from "lucide-react";

export function Header({ transparent = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLanding = location.pathname === "/";

  return (
    <header
      className={`h-14 flex items-center px-6 md:px-12 border-b z-50 ${
        transparent
          ? "bg-white/70 backdrop-blur-xl border-slate-100"
          : "bg-white/70 backdrop-blur-xl border-slate-200"
      }`}
      data-testid="main-header"
    >
      <Link to="/" className="flex items-center gap-2.5 mr-8">
        <div className="w-7 h-7 bg-slate-900 rounded flex items-center justify-center">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 4L8 2L14 4V12L8 14L2 12V4Z" stroke="white" strokeWidth="1.5" fill="none" />
            <path d="M8 2V14M2 4L14 4M2 12L14 12" stroke="white" strokeWidth="0.8" opacity="0.5" />
          </svg>
        </div>
        <span className="font-semibold text-slate-900 font-heading text-[15px]">DiagramFoundry</span>
      </Link>

      <nav className="hidden md:flex items-center gap-6 text-sm">
        {!isLanding && (
          <>
            <Link
              to="/dashboard"
              className={`transition-colors ${
                location.pathname === "/dashboard" ? "text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              Dashboard
            </Link>
            <Link
              to="/editor"
              className={`transition-colors ${
                location.pathname.startsWith("/editor") ? "text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              Editor
            </Link>
          </>
        )}
        <Link
          to="/gallery"
          className={`transition-colors ${
            location.pathname === "/gallery" ? "text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900"
          }`}
        >
          Gallery
        </Link>
        <Link
          to="/pricing"
          className={`transition-colors ${
            location.pathname === "/pricing" ? "text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900"
          }`}
        >
          Pricing
        </Link>
        <Link
          to="/extensions"
          className={`transition-colors ${
            location.pathname === "/extensions" ? "text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900"
          }`}
        >
          Extensions
        </Link>
      </nav>

      <div className="flex-1" />

      {user ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
              <div className="w-6 h-6 bg-slate-900 rounded-full flex items-center justify-center text-[11px] font-medium text-white">
                {user.username?.charAt(0).toUpperCase()}
              </div>
              <span className="hidden md:inline text-sm text-slate-700">{user.username}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard-btn">
              <Layout className="h-4 w-4 mr-2" /> Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-pricing-btn">
              <CreditCard className="h-4 w-4 mr-2" /> Subscription
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/extensions")} data-testid="menu-extensions-btn">
              <Puzzle className="h-4 w-4 mr-2" /> Extensions
            </DropdownMenuItem>
            {user?.is_admin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin-btn">
                  <ShieldCheck className="h-4 w-4 mr-2" /> Admin Panel
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                logout();
                navigate("/");
              }}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/auth")} data-testid="header-login-btn">
            Sign In
          </Button>
          <Button size="sm" onClick={() => navigate("/auth")} data-testid="header-signup-btn">
            Get Started
          </Button>
        </div>
      )}
    </header>
  );
}

export default Header;
